import { Howl, Howler } from 'howler'
import { paths, audio } from '../config/settings'
import { startSound, safeFadeTo } from './audioUtils'
import { state, setChosenMusicIdx, getChosenMusicIdx } from './audioState'
import { getRandomInt } from '../utils/math'
import { isIOS, isAndroid } from '../helpers/features'

let tleskacSound: Howl | null = null
let tleskacTimeoutId: number | null = null

// Music functions
export function playMusic(name: string, volume: number = 1, index: number): Howl {
  const sound = startSound('music', paths.AUDIO_MUSIC_PATH, name, {
    loop: false,
    volume,
    html5: false, // Use WebAudio for better volume control
  })

  let nextIndex = index + 1
  if (nextIndex >= audio.music.length) nextIndex = 0

  sound.on('end', () => {
    state.active.music?.sound.unload()
    state.active.music = null
    const next = audio.music[nextIndex]
    playMusic(next.name, next.volume, nextIndex)
  })

  return sound
}

export function toggleMusic(): void {
  const music = state.active.music?.sound
  if (!music) return

  if (music.playing()) {
    music.pause()
  } else {
    music.play()
  }
}

export function fadeMusicVolumeUp(fadeTime: number = 500): void {
  const entry = state.active.music
  if (!entry || !entry.sound) return
  const target = entry.volume ?? 1
  safeFadeTo(entry.sound, target, fadeTime)
}

export function fadeMusicVolumeDown(targetVolume: number = 0, fadeTime: number = 350): void {
  const { ambient, music } = state.active
  if (music && music.sound) {
    safeFadeTo(music.sound, targetVolume, fadeTime)
  }
  if (ambient && ambient.sound) {
    safeFadeTo(ambient.sound, targetVolume, fadeTime)
  }
}

export function startMusic(): void {
  if (getChosenMusicIdx() == null) {
    setChosenMusicIdx(getRandomInt(0, audio.music.length - 1))
  }
  const idx = getChosenMusicIdx()!
  const m = audio.music[idx]
  const sound = playMusic(m.name, m.volume, idx)

  // Apply stored music state from localStorage
  // If user had music paused, pause it immediately after starting
  try {
    const stored = localStorage.getItem('music_active')
    if (stored === 'false') {
      // User had music paused, so pause it now
      sound.pause()
    }
  } catch (err) {
    // Ignore localStorage errors (e.g., in private browsing mode), just continue with default behavior
  }
}

// Ambient functions
// export function playAmbient(name: string): Howl {
//   return startSound('ambient', paths.AUDIO_AMBIENT_PATH, name, {
//     loop: true,
//     html5: false, // Use WebAudio for better volume control
//     volume: audio.ambient.volume,
//   })
// }

// Item SFX functions
export function playItemSfx(itemName: string, onComplete: (() => unknown) | null = null): Howl | null {
  const meta = state.sfxMeta[itemName]
  // if (!meta) throw new Error(`AudioManager: SFX not in config: ${itemName}`)
  if (!meta) return null

  // Cycle through frames, but always start from 1 on page refresh (actual is initialized to 0)
  meta.actual = (meta.actual % meta.count) + 1
  const clipName = `${itemName}_${meta.actual}`

  let itemSound = state.itemSoundPool[clipName]

  if (!itemSound) {
    itemSound = new Howl({
      html5: false, // Use WebAudio for consistent volume control
      src: [`${paths.AUDIO_ITEMS_PATH}${clipName}.mp3`, `${paths.AUDIO_ITEMS_PATH}${clipName}.ogg`],
    })
    itemSound.on('loaderror', () => {})
    itemSound.on('playerror', () => {})
    state.itemSoundPool[clipName] = itemSound
  }

  // If this is the same sound that's currently playing or fading, stop it immediately (no fade) so we can replay it
  const isSameSound = state.currentItemSound === itemSound
  const isCurrentlyPlaying = itemSound.playing()

  if (isSameSound || isCurrentlyPlaying) {
    // Stop immediately without fade so we can replay right away
    // Remove any pending event handlers to prevent interference
    itemSound.off('fade')
    itemSound.off('end')
    if (isCurrentlyPlaying) {
      itemSound.stop()
    }
    state.currentItemSound = null
  } else {
    // Stop any other currently playing sound with fade
    stopItemSfx()
  }

  const startPlay = () => {
    // Remove any existing event handlers to prevent duplicate handlers
    itemSound.off('end')
    itemSound.off('fade')

    // Reset the sound to the beginning in case it was stopped mid-play
    if (itemSound.seek() > 0) {
      itemSound.seek(0)
    }
    itemSound.volume(audio.ITEMS_VOLUME)

    // On mobile, ensure audio context is resumed before playing
    const doPlay = () => {
      // Only set currentItemSound after we successfully start playing
      const playId = itemSound.play()
      if (playId !== undefined) {
        state.currentItemSound = itemSound

        // Set up the 'end' handler after we've successfully started playing
        itemSound.once('end', () => {
          onComplete?.()
          fadeMusicVolumeUp()
          // Only clear currentItemSound if this is still the current sound
          if (state.currentItemSound === itemSound) {
            state.currentItemSound = null
          }
        })
      }
    }

    if (isIOS || isAndroid) {
      // Ensure audio context is resumed if suspended
      if (Howler.ctx && Howler.ctx.state === 'suspended') {
        Howler.ctx
          .resume()
          .then(() => {
            doPlay()
          })
          .catch(() => {
            // If resume fails, try to play anyway
            doPlay()
          })
      } else {
        doPlay()
      }
    } else {
      doPlay()
    }
  }

  fadeMusicVolumeDown(meta.backgroundMusicVolume)

  if (itemSound.state() === 'loaded') {
    startPlay()
  } else {
    itemSound.once('load', startPlay)
    // Ensure loading starts
    if (itemSound.state() === 'unloaded') {
      itemSound.load()
    }
  }
  return itemSound
}

export function stopItemSfx(fadeMs: number = 700): void {
  const sound = state.currentItemSound
  if (!sound) {
    fadeMusicVolumeUp()
    return
  }

  // Remove any existing event handlers to prevent conflicts
  sound.off('fade')
  sound.off('end')

  // If fadeMs is 0 or less, stop immediately
  if (fadeMs <= 0) {
    sound.stop()
    state.currentItemSound = null
    fadeMusicVolumeUp()
    return
  }

  // Clear currentItemSound immediately so playItemSfx can start a new sound
  state.currentItemSound = null

  sound.fade(sound.volume(), 0, fadeMs)
  sound.once('fade', () => {
    sound.stop()
    // keep it loaded in the pool for later reuse
  })
  fadeMusicVolumeUp()
}

// General SFX functions
export function playSfx(sfxName: string, volume: number = 1): Howl {
  const sound = new Howl({
    src: [`${paths.AUDIO_SFX_PATH}${sfxName}.mp3`, `${paths.AUDIO_SFX_PATH}${sfxName}.ogg`],
    volume,
    html5: false, // Use WebAudio for consistent behavior
  })

  // On iOS/Android, we need to ensure the audio context is resumed and sound is loaded before playing
  // This is necessary because mobile browsers require sounds to be loaded
  // and the audio context to be active before they can be played
  if (isIOS || isAndroid) {
    // Ensure audio context is resumed if suspended
    const resumeContext = () => {
      if (Howler.ctx && Howler.ctx.state === 'suspended') {
        return Howler.ctx.resume().catch(() => {})
      }
      return Promise.resolve()
    }

    if (sound.state() === 'unloaded') {
      // Sound needs to load first - wait for load then play
      sound.once('load', () => {
        resumeContext().then(() => {
          sound.play()
        })
      })
      sound.load() // Start loading
    } else {
      // Sound is already loaded - resume context then play
      resumeContext().then(() => {
        sound.play()
      })
    }
  } else {
    // Not iOS/Android - play immediately
    sound.play()
  }

  return sound
}
